import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  SpaceBetween,
  Container,
  Header,
  Button,
  Alert,
  Spinner,
  StatusIndicator,
  ColumnLayout,
  Link,
  Flashbar,
} from "@cloudscape-design/components";
import { useTranslation } from "react-i18next";
import BaseLayout from "./BaseLayout";
import API from "../api";

const MAX_POLL_ATTEMPTS = 20;

// UPayments appends these fields as query params on the return URL
const UPAYMENTS_FIELDS = [
  "payment_id", "result", "post_date", "tran_id", "ref",
  "track_id", "auth", "order_id", "requested_order_id",
  "refund_order_id", "payment_type", "invoice_id",
  "transaction_date", "receipt_id", "trn_udf",
];

const PaymentResult = () => {
  const { t } = useTranslation();

  const [paymentStatus, setPaymentStatus] = useState("loading");
  const [paymentData,   setPaymentData]   = useState(null);

  const [policyStage,   setPolicyStage]   = useState("idle");
  const [policyLinks,   setPolicyLinks]   = useState([]);
  const [policyNumbers, setPolicyNumbers] = useState([]);

  const [retrying, setRetrying] = useState(false);
  const [flashItems, setFlashItems] = useState([]);

  const policyTriggered = useRef(false);
  const pollCount       = useRef(0);

  const params = new URLSearchParams(window.location.search);

  // UPayments sometimes appends its params with '?' instead of '&', which
  // embeds '?payment_id=...' inside the order_id value. Strip the suffix and
  // fall back to requested_order_id (which UPayments sets correctly).
  let rawOrderId = params.get("order_id") || "";
  if (rawOrderId.includes("?")) {
    rawOrderId = rawOrderId.split("?")[0];
  }
  const orderId   = rawOrderId || params.get("requested_order_id") || "";
  const cancelled = params.get("cancelled") === "true";

  // ─── Step 1: Send UPayments return-URL params to backend ─────────────────
  const sendCallbackData = () => {
    const callbackData = { order_id: orderId };
    let hasData = false;

    UPAYMENTS_FIELDS.forEach((field) => {
      const val = params.get(field);
      if (val) {
        callbackData[field] = val;
        hasData = true;
      }
    });

    if (!hasData) return Promise.resolve();

    return API.post("/payment/callback", callbackData).catch(() => {});
  };

  // ─── Step 2: Poll backend for verified status ─────────────────────────────
  const checkPaymentStatus = () => {
    if (!orderId) { setPaymentStatus("error"); return; }

    pollCount.current += 1;
    if (pollCount.current > MAX_POLL_ATTEMPTS) {
      setPaymentStatus("error");
      return;
    }

    API.get(`/payment/status/${orderId}`)
      .then((response) => {
        if (!response.data.operation) { setPaymentStatus("error"); return; }

        const data = response.data.data;
        setPaymentData(data);

        if (data.status === "success") {
          setPaymentStatus("success");
          if (data.policy_generated) {
            setPolicyStage("already_done");
            fetchPolicyLinks();
          } else if (!policyTriggered.current) {
            policyTriggered.current = true;
            triggerPolicyCreation();
          }
        } else if (data.status === "pending") {
          setPaymentStatus("loading");
          setTimeout(checkPaymentStatus, 3000);
        } else if (data.status === "cancelled") {
          setPaymentStatus("cancelled");
        } else {
          setPaymentStatus("failed");
        }
      })
      .catch(() => setPaymentStatus("error"));
  };

  // ─── Fetch policy links (page revisit) ───────────────────────────────────
  const fetchPolicyLinks = () => {
    API.get(`/payment/policy-links/${orderId}`)
      .then((res) => {
        if (res.data.operation && res.data.data) {
          setPolicyLinks(res.data.data.links   || []);
          setPolicyNumbers(res.data.data.polnos || []);
        }
      })
      .catch(() => {});
  };

  // ─── Step 3: Create policy after verified success ─────────────────────────
  const triggerPolicyCreation = () => {
    setPolicyStage("creating");

    const savedForm = sessionStorage.getItem("ci_payment_form");
    if (!savedForm) {
      setPolicyStage("error");
      setFlashItems([{ type: "error", content: t("payment_no_form_data"),
        dismissible: true, onDismiss: () => setFlashItems([]) }]);
      return;
    }

    let formData;
    try { formData = JSON.parse(savedForm); }
    catch { setPolicyStage("error"); return; }

    API.post("/payment/create-policy", {
      order_id:       orderId,
      create_payload: formData.create_payload,
    })
      .then((response) => {
        if (response.data.operation === true) {
          const result = response.data.data;
          setPolicyLinks(result.links   || []);
          setPolicyNumbers(result.polnos || []);
          setPolicyStage("done");
          sessionStorage.removeItem("ci_payment_form");
        } else {
          setPolicyStage("error");
          setFlashItems([{ type: "error",
            content: response.data.message || t("payment_policy_error"),
            dismissible: true, onDismiss: () => setFlashItems([]) }]);
        }
      })
      .catch((error) => {
        setPolicyStage("error");
        setFlashItems([{ type: "error",
          content: error.response?.data?.message || error.message || t("payment_policy_error"),
          dismissible: true, onDismiss: () => setFlashItems([]) }]);
      });
  };

  // ─── Retry payment ────────────────────────────────────────────────────────
  const handleRetryPayment = () => {
    setRetrying(true);
    const savedForm = sessionStorage.getItem("ci_payment_form");
    if (!savedForm) { setRetrying(false); window.location.href = "/get_quote"; return; }

    try {
      const formData = JSON.parse(savedForm);
      API.post("/payment/initiate", {
        amount:       formData.amount || paymentData?.amount || 0,
        currency:     paymentData?.currency || "KWD",
        customer_name:  paymentData?.customer_name || "",
        customer_email: formData.create_payload?.contact?.contactEmail || "",
        customer_phone: formData.create_payload?.contact?.contactNumber || "",
        reference_id:   formData.reference_id || paymentData?.reference_id || "",
        payment_type:   formData.create_payload?.payment_type || "Individual",
        // reuse the stored quote if there is one, otherwise send the form so it gets stored now
        request_id:     formData.request_id || undefined,
        quote:          formData.request_id ? undefined : formData.create_payload,
      })
        .then((response) => {
          if (response.data.operation) {
            formData.order_id = response.data.data.order_id;
            formData.request_id = response.data.data.request_id || formData.request_id;
            sessionStorage.setItem("ci_payment_form", JSON.stringify(formData));
            window.location.href = response.data.data.payment_link;
          } else {
            setRetrying(false);
            setFlashItems([{ type: "error",
              content: response.data.message || "Retry failed.",
              dismissible: true, onDismiss: () => setFlashItems([]) }]);
          }
        })
        .catch((err) => {
          setRetrying(false);
          setFlashItems([{ type: "error",
            content: err.response?.data?.message || "Retry failed.",
            dismissible: true, onDismiss: () => setFlashItems([]) }]);
        });
    } catch {
      setRetrying(false);
      window.location.href = "/get_quote";
    }
  };

  useEffect(() => {
    if (cancelled) {
      setPaymentStatus("cancelled");
      return;
    }
    // First persist UPayments params, then poll status
    sendCallbackData().then(checkPaymentStatus);
  }, []);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <BaseLayout content={
      <Box padding={{ top: "xl", horizontal: "l" }}>
        <SpaceBetween size="l">
          {flashItems.length > 0 && <Flashbar items={flashItems} />}

          <Container header={<Header variant="h2">{t("payment_result_title")}</Header>}>
            <SpaceBetween size="l">

              {/* Status indicator */}
              <Box textAlign="center" padding="l">
                {paymentStatus === "loading" && (
                  <SpaceBetween size="s" alignItems="center">
                    <Spinner size="large" />
                    <StatusIndicator type="in-progress">{t("payment_verifying")}</StatusIndicator>
                  </SpaceBetween>
                )}
                {paymentStatus === "success"   && <StatusIndicator type="success">{t("payment_success")}</StatusIndicator>}
                {paymentStatus === "failed"    && <StatusIndicator type="error">{t("payment_failed")}</StatusIndicator>}
                {paymentStatus === "cancelled" && <StatusIndicator type="warning">{t("payment_cancelled")}</StatusIndicator>}
                {paymentStatus === "error"     && <StatusIndicator type="error">{t("payment_error")}</StatusIndicator>}
              </Box>

              {/* Transaction details */}
              {paymentData && (
                <Container header={<Header variant="h3">{t("payment_details")}</Header>}>
                  <ColumnLayout columns={2} variant="text-grid">
                    <div><Box variant="awsui-key-label">{t("payment_order_id")}</Box><div>{paymentData.order_id}</div></div>
                    <div><Box variant="awsui-key-label">{t("payment_amount")}</Box><div>{paymentData.currency} {paymentData.amount}</div></div>
                    <div><Box variant="awsui-key-label">{t("payment_status_label")}</Box><div>{paymentData.status?.toUpperCase()}</div></div>
                    {paymentData.transaction_id && (
                      <div><Box variant="awsui-key-label">{t("payment_transaction_id")}</Box><div>{paymentData.transaction_id}</div></div>
                    )}
                    {paymentData.payment_id && (
                      <div><Box variant="awsui-key-label">Payment ID</Box><div>{paymentData.payment_id}</div></div>
                    )}
                    {paymentData.track_id && (
                      <div><Box variant="awsui-key-label">Track ID</Box><div>{paymentData.track_id}</div></div>
                    )}
                    {paymentData.ref && (
                      <div><Box variant="awsui-key-label">Reference</Box><div>{paymentData.ref}</div></div>
                    )}
                    {paymentData.auth && (
                      <div><Box variant="awsui-key-label">Auth Code</Box><div>{paymentData.auth}</div></div>
                    )}
                    {paymentData.invoice_id && (
                      <div><Box variant="awsui-key-label">Invoice ID</Box><div>{paymentData.invoice_id}</div></div>
                    )}
                    {paymentData.receipt_id && (
                      <div><Box variant="awsui-key-label">Receipt ID</Box><div>{paymentData.receipt_id}</div></div>
                    )}
                    {paymentData.transaction_date && (
                      <div><Box variant="awsui-key-label">Transaction Date</Box><div>{paymentData.transaction_date}</div></div>
                    )}
                    <div><Box variant="awsui-key-label">{t("payment_customer")}</Box><div>{paymentData.customer_name}</div></div>
                  </ColumnLayout>
                </Container>
              )}

              {/* Policy creation progress */}
              {paymentStatus === "success" && policyStage === "creating" && (
                <Alert type="info">
                  <SpaceBetween direction="horizontal" size="xs">
                    <Spinner /><span>{t("payment_creating_policy")}</span>
                  </SpaceBetween>
                </Alert>
              )}

              {/* Policy ready */}
              {paymentStatus === "success" && (policyStage === "done" || policyStage === "already_done") && (
                <Container header={<Header variant="h3">{t("payment_policy_ready")}</Header>}>
                  <SpaceBetween size="m">
                    <Alert type={policyStage === "done" ? "success" : "info"}>
                      {policyStage === "done" ? t("payment_policy_success_msg") : t("payment_policy_already_generated")}
                    </Alert>
                    {policyNumbers.length > 0 && (
                      <div>
                        <Box variant="awsui-key-label">{t("flash_policy_numbers")}</Box>
                        <div>{policyNumbers.join(", ")}</div>
                      </div>
                    )}
                    {policyLinks.length > 0 && (
                      <SpaceBetween size="xs">
                        <Box variant="awsui-key-label">{t("payment_download_policies")}</Box>
                        {policyLinks.map((link, idx) => (
                          <Link key={idx} href={link} external target="_blank">
                            {policyNumbers[idx] || `Policy ${idx + 1}`}
                          </Link>
                        ))}
                      </SpaceBetween>
                    )}
                  </SpaceBetween>
                </Container>
              )}

              {/* Policy error */}
              {paymentStatus === "success" && policyStage === "error" && (
                <Alert type="error">
                  <SpaceBetween direction="horizontal" size="s">
                    <span>{t("payment_policy_error")}</span>
                    <Button
                      variant="inline-link"
                      onClick={() => {
                        policyTriggered.current = false;
                        setPolicyStage("idle");
                        triggerPolicyCreation();
                      }}
                    >
                      {t("payment_retry_policy")}
                    </Button>
                  </SpaceBetween>
                </Alert>
              )}

              {/* Failed / cancelled messages */}
              {paymentStatus === "failed"    && <Alert type="error">{t("payment_failed_msg")}</Alert>}
              {paymentStatus === "cancelled" && <Alert type="warning">{t("payment_cancelled_msg")}</Alert>}
              {paymentStatus === "error"     && <Alert type="error">{t("payment_error_msg")}</Alert>}

              {/* Action buttons */}
              <Box textAlign="center">
                <SpaceBetween direction="horizontal" size="xs">
                  {(paymentStatus === "failed" || paymentStatus === "cancelled") && (
                    <Button variant="primary" onClick={handleRetryPayment}
                      loading={retrying} disabled={retrying}>
                      {t("payment_retry")}
                    </Button>
                  )}
                  <Button variant="link" onClick={() => (window.location.href = "/get_quote")}>
                    {t("payment_back_to_quote")}
                  </Button>
                </SpaceBetween>
              </Box>

            </SpaceBetween>
          </Container>
        </SpaceBetween>
      </Box>
    } />
  );
};

export default PaymentResult;
